import styled from 'styled-components';
import ItemCard from './components/ItemCard';
import MultiCard from './components/MultiCard';
import { useState, useEffect } from 'react';
import AppHeader from './components/AppHeader';
import { reqGet } from '../service';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  ModalAtomState,
  ListAtomState,
  DisplayListAtomState,
  CurrentStepAtomState,
  SelectedGroupsAtomState,
} from '../recoil/ModalAtom';
import Popup from '../components/Popup';
import { formatManWon } from '../utils/formatNumber';
import { useAuth } from '../contexts/AuthContext';
import Login from '../components/Login';
import Stepper, { STEPS } from './components/Stepper';
import LeftSidebar from './components/LeftSidebar';
import RightSummary from './components/RightSummary';
import Step1Groups from './components/Step1Groups';
import Step4Review from './components/Step4Review';
import TeamWizardApp from './components/TeamWizardApp';
import { ActiveTeamAtom } from './recoil/teamAtoms';

const REQUIRED_GROUP_KEY = 'infraSetting';

const AppContainer = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.bg};
  color: ${p => p.theme.text};
  display: flex;
  flex-direction: column;
`;

const Body = styled.div`
  display: flex;
  gap: 20px;
  padding: 20px 24px;
  align-items: flex-start;
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;

  @media screen and (max-width: 1024px) {
    flex-direction: column;
    padding: 12px;
    gap: 12px;
  }
  @media screen and (max-width: 380px) {
    padding: 8px;
    gap: 8px;
  }
`;

const Center = styled.main`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const ContentCard = styled.div`
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radius};
  padding: 24px;
  min-height: 400px;

  @media screen and (max-width: 768px) {
    padding: 16px;
    min-height: 280px;
  }
  @media screen and (max-width: 380px) {
    padding: 12px;
  }
`;

const StepNav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radius};
  gap: 8px;

  @media screen and (max-width: 768px) {
    padding: 12px 14px;
  }
`;

const NavButton = styled.button`
  padding: 10px 20px;
  border-radius: ${p => p.theme.radiusSm};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: ${p => p.theme.transition};
  border: 1px solid ${p => p.$primary ? p.theme.accent : p.theme.border};
  background: ${p => p.$primary ? p.theme.accent : p.theme.bgCard};
  color: ${p => p.$primary ? '#fff' : p.theme.textSecondary};
  flex-shrink: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${p => p.$primary ? p.theme.accentHover : p.theme.bgHover};
  }

  @media screen and (max-width: 768px) {
    padding: 8px 14px;
    font-size: 13px;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 700;
  color: ${p => p.theme.text};
`;

const SectionSub = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  color: ${p => p.theme.textMuted};
`;

const InfoBar = styled.div`
  background: ${p => p.theme.accentLight};
  color: ${p => p.theme.accent};
  font-size: 12px;
  padding: 12px 14px;
  border-radius: ${p => p.theme.radiusSm};
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: ${p => p.theme.bg};
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid ${p => p.theme.border};
  border-top: 4px solid ${p => p.theme.accent};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${p => p.theme.textSecondary};
  margin-top: 16px;
  font-size: 16px;
`;

function App() {
  const { isLoggedIn, isLoading } = useAuth();
  const activeTeam = useRecoilValue(ActiveTeamAtom);
  const [DisplayList, setDisplayList] = useRecoilState(DisplayListAtomState);
  const ModalInfo = useRecoilValue(ModalAtomState);
  const [ItemStack, setItemStack] = useRecoilState(ListAtomState);
  const [currentStep, setCurrentStep] = useRecoilState(CurrentStepAtomState);
  const [selectedGroups, setSelectedGroups] = useRecoilState(SelectedGroupsAtomState);

  const [categoryCheck, setcategoryCheck] = useState(['인프라']);
  const [multiCategoryCheck, setMultiCategoryCheck] = useState(['인프라']);
  const [SumItemStack, setSumItemStack] = useState([]);
  const [GroupList, setGroupList] = useState([]);
  const [strTotalValue, setStrTotalValue] = useState(0);

  useEffect(() => {
    if (isLoggedIn) {
      getGroupList();
    }
  }, [isLoggedIn]);

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>로딩 중...</LoadingText>
      </LoadingContainer>
    );
  }

  if (!isLoggedIn) {
    return <Login />;
  }

  // 활성팀이 있으면 팀 템플릿 wizard 렌더 (시스템 wizard 와 완전 분리)
  if (activeTeam) {
    return <TeamWizardApp />;
  }

  const getGroupList = () => {
    reqGet('/api/groupList', {}, (res) => setGroupList(res));
  };

  const SumChildStack = (e) => {
    setSumItemStack(ItemStack.concat(e));
  };

  const checkItem = (e) => {
    var NewItemStack = [...ItemStack];
    var NewDisplayList = [...DisplayList];
    const itemIndex = NewItemStack.findIndex(item => item.Key === e.Key);
    const itemNameIndex = NewItemStack.findIndex(item => item.NAME === e.NAME);

    if ((itemIndex !== -1) && (itemNameIndex !== -1)) {
      var SubValue = strTotalValue;
      for (var i = itemIndex; i < NewItemStack.length; i++) {
        SubValue -= NewItemStack[i].PRICE;
      }
      setStrTotalValue(SubValue);
      NewItemStack.splice(itemNameIndex);
      if (NewDisplayList[itemNameIndex] === e.Key) {
        NewDisplayList.splice(itemNameIndex + 1);
      }
    } else if ((itemIndex !== -1) && (itemNameIndex === -1)) {
      var SubValue2 = strTotalValue;
      for (var j = itemIndex; j < NewItemStack.length; j++) {
        SubValue2 -= NewItemStack[j].PRICE;
      }
      setStrTotalValue(SubValue2 + e.PRICE);
      NewItemStack.splice(itemIndex);
      NewItemStack.push(e);
      if (NewDisplayList[itemIndex] !== e.NEXT) {
        NewDisplayList.splice(itemIndex + 1);
      }
      NewDisplayList.push(e.NEXT);
    } else {
      NewItemStack.push(e);
      setStrTotalValue(strTotalValue + e.PRICE);
      NewDisplayList.push(e.NEXT);
    }
    setItemStack(NewItemStack);
    setSumItemStack(NewItemStack);
    setcategoryCheck(NewDisplayList);
    setDisplayList(NewDisplayList);
  };

  const checkMultiItem = (e) => {
    var NewItemStack = [...ItemStack];
    var NewDisplayList = [...DisplayList];
    const itemIndex = NewItemStack.findIndex(item => item.NAME === e.NAME);

    if (itemIndex !== -1) {
      var SubValue = strTotalValue - NewItemStack[itemIndex].PRICE;
      setStrTotalValue(SubValue);
      NewItemStack.splice(itemIndex, 1);
      if (NewDisplayList[itemIndex] === e.Key) {
        NewDisplayList.splice(itemIndex + 1);
      }
    } else {
      NewItemStack.push(e);
      setStrTotalValue(strTotalValue + e.PRICE);
      NewDisplayList.push(e.NEXT);
    }
    setItemStack(NewItemStack);
    setSumItemStack(NewItemStack);
    setcategoryCheck(NewDisplayList);
    setDisplayList(NewDisplayList);
  };

  const handleResetAll = () => {
    setItemStack([]);
    setSumItemStack([]);
    setStrTotalValue(0);
    setDisplayList(['인프라']);
    setcategoryCheck(['인프라']);
    setMultiCategoryCheck(['인프라']);
    setSelectedGroups([REQUIRED_GROUP_KEY]);
    setCurrentStep(1);
  };

  const goPrev = () => setCurrentStep(s => Math.max(1, s - 1));
  const goNext = () => setCurrentStep(s => Math.min(STEPS.length, s + 1));

  // 2~3단계에 표시할 그룹 필터링
  const groupsForStep = (() => {
    if (currentStep === 2) {
      return GroupList.filter(g => g.std_key === REQUIRED_GROUP_KEY && selectedGroups.includes(g.std_key));
    }
    if (currentStep === 3) {
      return GroupList.filter(g => g.std_key !== REQUIRED_GROUP_KEY && selectedGroups.includes(g.std_key));
    }
    return [];
  })();

  const renderGroupCards = (group, idx) => (
    <div key={idx} style={{ marginBottom: 24 }}>
      <SectionTitle>{group.std_value}</SectionTitle>
      {group.std_key === 'infraSetting' ? (
        <ItemCard
          Key={group.std_key}
          calcValue={(e) => checkItem(e)}
          ItemStack={ItemStack}
          categoryCheck={categoryCheck}
          SumItemStack={SumItemStack}
          groupChoice={selectedGroups}
          SubCategory={DisplayList}
        />
      ) : (
        <MultiCard
          Key={group.std_key}
          calcValue={(e) => checkMultiItem(e)}
          groupChoice={selectedGroups}
          SumItemStack={SumItemStack}
          setSumItemStack={(e) => setSumItemStack(e)}
          setStrTotalValue={(e) => setStrTotalValue(e)}
          strTotalValue={strTotalValue}
          multiCategoryCheck={multiCategoryCheck}
          setMultiCategoryCheck={setMultiCategoryCheck}
          categoryCheck={categoryCheck}
          setcategoryCheck={setcategoryCheck}
          ItemStack={ItemStack}
          SumChildStack={SumChildStack}
          SubCategory={DisplayList}
        />
      )}
    </div>
  );

  return (
    <AppContainer>
      <AppHeader />
      <Stepper current={currentStep} onChange={setCurrentStep} />

      <Body>
        <LeftSidebar groupList={GroupList} $hideOnMobileWhenAdvanced={currentStep > 1} />

        <Center>
          <ContentCard>
            {currentStep === 1 && <Step1Groups groupList={GroupList} />}

            {currentStep === 2 && (
              <>
                <SectionTitle>개발환경 선택</SectionTitle>
                <SectionSub>서비스 운영 환경(인프라/플랫폼/웹엔진 등)을 선택해주세요.</SectionSub>
                {groupsForStep.length === 0 ? (
                  <InfoBar>1단계에서 "개발환경 선택" 그룹을 활성화한 뒤 이용해주세요.</InfoBar>
                ) : (
                  groupsForStep.map(renderGroupCards)
                )}
              </>
            )}

            {currentStep === 3 && (
              <>
                <SectionTitle>기능 선택</SectionTitle>
                <SectionSub>선택하신 그룹에 포함된 기능들을 추가해주세요.</SectionSub>
                {groupsForStep.length === 0 ? (
                  <InfoBar>1단계에서 기능 그룹을 선택하면 이곳에 표시됩니다.</InfoBar>
                ) : (
                  groupsForStep.map(renderGroupCards)
                )}
              </>
            )}

            {currentStep === 4 && <Step4Review />}
          </ContentCard>

          <StepNav>
            <NavButton onClick={goPrev} disabled={currentStep === 1}>이전 단계</NavButton>
            <span style={{ fontSize: 13, color: '#94a3b8' }}>
              합계 {formatManWon(strTotalValue)}만원 (VAT 별도)
            </span>
            {currentStep < STEPS.length ? (
              <NavButton $primary onClick={goNext}>다음 단계</NavButton>
            ) : (
              <NavButton $primary onClick={() => alert('견적이 완료되었습니다.')}>완료</NavButton>
            )}
          </StepNav>
        </Center>

        <RightSummary groupList={GroupList} onResetAll={handleResetAll} />
      </Body>

      <Popup.Modal ModalInfo={ModalInfo} />
    </AppContainer>
  );
}

export default App;
